import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'
import { Assets } from '@nexds/web'
import { CSSProperties } from 'styled-components'

import { BetaPill } from '../BetaPill'
import { ScrollButton, TabBadge, TabContainer, TabItem, TabScrollContainer, TabSkeleton } from './Tabs.styles'

import { MetricsEvents } from '@/constants/metricsEvents'
import { sendMetrics } from '@/presentation/utils/sendMetrics'

type Tab = { label: string; hasError?: boolean; count?: number }

interface TabsProps extends HTMLAttributes<HTMLDivElement> {
  tabs: Record<string, Tab>
  currentTab: string
  setCurrentTab: (tab: string) => void
  size?: 'sm' | 'md' | 'lg'
  hasScrollButtons?: boolean
  textTransform?: CSSProperties['textTransform']
  isPositionSticky?: boolean
  isLoading?: boolean
  betaTabs?: string[]
  /**
   * Identificador do grupo de abas em `PLANNINGAPP_TAB_SELECTED`. Preencher apenas em grupos
   * cuja troca de aba NÃO muda a rota — quando muda, o `PAGE_ENTERED` já mede.
   */
  metricKey?: string
}

function Tabs(props: TabsProps) {
  const {
    hasScrollButtons = false,
    tabs,
    currentTab,
    setCurrentTab,
    size = 'md',
    textTransform = 'uppercase',
    isPositionSticky = false,
    isLoading = false,
    metricKey,
    ...rest
  } = props
  const handleTabClick = useCallback(
    (key: string) => {
      // Clicar na aba já ativa não é troca de aba: não emite.
      if (metricKey && key !== currentTab) {
        sendMetrics(MetricsEvents.TAB_SELECTED, { group: metricKey, tab: key })
      }

      setCurrentTab(key)
    },
    [metricKey, currentTab, setCurrentTab]
  )

  const tabContainerRef = useRef<HTMLDivElement>(null)
  const [scrollState, setScrollState] = useState({ canScrollLeft: false, canScrollRight: false })

  const updateScrollState = useCallback(() => {
    const container = tabContainerRef.current
    if (!container || !hasScrollButtons) return

    const { scrollLeft, clientWidth, scrollWidth } = container
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0)

    setScrollState((prev) => {
      const next = {
        canScrollLeft: scrollLeft > 0,
        canScrollRight: scrollWidth > clientWidth && scrollLeft < maxScrollLeft - 1
      }

      if (prev.canScrollLeft === next.canScrollLeft && prev.canScrollRight === next.canScrollRight) {
        return prev
      }

      return next
    })
  }, [hasScrollButtons])

  useEffect(() => {
    if (tabContainerRef.current && hasScrollButtons) {
      const activeTab = tabContainerRef.current.querySelector('.active') as HTMLElement
      if (activeTab) {
        const tabElement = activeTab
        const tabWidth = tabElement.clientWidth
        const containerWidth = tabContainerRef.current.clientWidth
        const scrollLeft = tabElement.offsetLeft - (containerWidth - tabWidth) / 2
        tabContainerRef.current.scrollTo({ left: scrollLeft, behavior: 'smooth' })
      }

      requestAnimationFrame(updateScrollState)
    }
  }, [currentTab, hasScrollButtons, updateScrollState])

  useEffect(() => {
    if (!hasScrollButtons) return

    const syncOnNextFrame = requestAnimationFrame(updateScrollState)
    window.addEventListener('resize', updateScrollState)

    return () => {
      cancelAnimationFrame(syncOnNextFrame)
      window.removeEventListener('resize', updateScrollState)
    }
  }, [tabs, hasScrollButtons, updateScrollState])

  return (
    <TabContainer isPositionSticky={isPositionSticky} {...rest}>
      {hasScrollButtons && scrollState.canScrollLeft && (
        <ScrollButton variant="left" onClick={() => tabContainerRef.current?.scrollTo({ left: 0, behavior: 'smooth' })}>
          <Assets.ChevronLeft color="#fff" size={16} />
        </ScrollButton>
      )}
      <TabScrollContainer
        ref={tabContainerRef}
        hasScrollButtons={hasScrollButtons}
        onScroll={() => updateScrollState()}
      >
        {isLoading ? (
          <>
            <TabSkeleton size={size} />
            <TabSkeleton size={size} />
            <TabSkeleton size={size} />
          </>
        ) : (
          Object.entries(tabs).map(([key, tab]) => (
            <TabItem
              key={key}
              className={currentTab === key ? 'active' : undefined}
              active={currentTab === key}
              size={size}
              textTransform={textTransform}
              error={tab.hasError}
              // Prevent the browser's focus-on-click from snapping the scroll to
              // reveal the button: that reset the scroll position before the
              // centering animation could run from where it currently is. Mouse
              // clicks no longer steal focus; keyboard focus still works.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleTabClick(key)}
            >
              {tab.label}
              {tab.count !== undefined && (
                <TabBadge active={currentTab === key}>{tab.count > 99 ? '99+' : tab.count}</TabBadge>
              )}
              {props.betaTabs?.includes(key) && <BetaPill label="BETA" position={{ top: '-9px', left: '-5px' }} />}
            </TabItem>
          ))
        )}
      </TabScrollContainer>
      {hasScrollButtons && scrollState.canScrollRight && (
        <ScrollButton
          variant="right"
          onClick={() => tabContainerRef.current?.scrollTo({ left: 9999, behavior: 'smooth' })}
        >
          <Assets.ChevronRight color="#fff" size={16} />
        </ScrollButton>
      )}
    </TabContainer>
  )
}

export { Tabs }
